import React, { useEffect, useState } from "react";
import "./Card.css";

/**
 * Shared card. Props API unchanged from the pre-redesign component
 * (title, subtitle, description, image, link, tag, tagType, className,
 * align) plus two optional slots: `icon` for service cards, and
 * `fallbackImage` for a local preview used when `image` is missing — or
 * when it fails to load, so a dead external host never leaves an empty
 * frame. See src/assets/products/README.md.
 */
const Card = ({
  title,
  subtitle,
  description,
  image,
  link,
  tag,
  tagType,
  className,
  align,
  icon,
  fallbackImage,
  onLinkClick,
}) => {
  const [src, setSrc] = useState(image || fallbackImage || null);

  useEffect(() => {
    setSrc(image || fallbackImage || null);
  }, [image, fallbackImage]);

  const onImageError = () => {
    if (fallbackImage && src !== fallbackImage) setSrc(fallbackImage);
    else setSrc(null);
  };

  const content = (
    <div
      className={`card ${className || ""} ${
        align ? `card-align-${align}` : ""
      }`.trim()}
    >
      {src && (
        <div className="card-image-container">
          <img
            src={src}
            alt=""
            className="card-image"
            loading="lazy"
            decoding="async"
            width="1200"
            height="675"
            onError={onImageError}
          />
        </div>
      )}
      <div className="card-content-container">
        {icon && <span className="card-icon">{icon}</span>}
        <h3 className="card-title">{title}</h3>
        {subtitle && <p className="card-subtitle">{subtitle}</p>}
        {description && <p className="card-description">{description}</p>}
      </div>

      {(link?.href || link?.label) && (
        <div className="card-link-container">
          <span className="card-link link-arrow">
            {(link.label || "Learn more →").replace(/\s*→\s*$/, "")}{" "}
            <span className="arrow">→</span>
          </span>
        </div>
      )}

      {tag && (
        <div className="card-tag-container">
          <span className={`card-tag ${tagType || ""}`.trim()}>{tag}</span>
        </div>
      )}
    </div>
  );

  if (link && link.href) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className="card-link-wrapper"
        aria-label={title}
        onClick={onLinkClick}
      >
        {content}
      </a>
    );
  }

  return content;
};

export default Card;
